import styled, { css } from 'styled-components';
import { Autocomplete as MUIAutocomplete, TextField, AutocompleteProps, Chip, Typography } from '@mui/material';
import { CloseGrayIcon, PaginationSelectIcon } from 'shared-ui';

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-height: 294px;
`;

export const Label = styled.label`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.text.primary};
  margin-bottom: 8px;
  display: block;
`;

export const Required = styled.span`
  color: ${({ theme }) => theme.palette.error.main};
`;

export const Autocomplete = styled(
  ({ className, ...props }: AutocompleteProps<unknown, true, true, true> & { className: string }) => (
    <MUIAutocomplete
      classes={{
        root: 'root',
        paper: className,
        option: 'option',
        listbox: 'listbox',
        clearIndicator: 'clearIcon',
      }}
      {...props}
    />
  ),
)(
  ({ theme: { palette, shape } }) => css`
    background-color: ${palette.grey.A200};
    box-shadow: none;
    border-radius: ${shape.borderRadius}px;
    border: 1px solid ${palette.grey[500]};

    & .listbox {
      padding: 0px;
    }

    & .option {
      color: ${palette.grey[900]};
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-start !important;
      border-bottom: 1px solid ${palette.grey[500]};
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
      padding: 8px !important;
      background-color: ${palette.grey.A200};

      &:last-child {
        border-bottom: none;
      }

      &:hover {
        color: ${palette.primary.main};
        background-color: ${palette.grey[300]};

        & ${OptionNumber}, & ${OptionLabel} {
          color: ${palette.primary.main};
        }
      }

      &[aria-selected='true'] {
        color: ${palette.primary.main};
        background-color: ${palette.grey.A200};

        & ${OptionNumber}, & ${OptionLabel} {
          color: ${palette.primary.main};
        }
      }

      &:focus {
        color: ${palette.primary.main};
        background-color: ${palette.grey.A200};
        outline: none;
      }
    }
  `,
);

export const StyledTextField = styled(TextField)(
  ({ theme: { palette, shape } }) => css`
    width: 100%;

    & .MuiFilledInput-root {
      background-color: ${palette.grey[100]};
      color: ${palette.text.primary};
      border-radius: ${shape.borderRadius}px;
      font-weight: 700;
      text-align: left;
      padding: 0;
      min-height: 48px;
    }

    & .MuiAutocomplete-input {
      padding: 0 !important;
    }

    & .MuiAutocomplete-inputRoot {
      padding: 4px 16px;
      width: 100%;
    }

    & ::placeholder {
      color: ${palette.text.primary};
      font-weight: 500;
      font-size: 14px;
      opacity: 1;
    }
  `,
);

export const ErrorText = styled.span`
  color: ${({ theme }) => theme.palette.error.main};
  font-size: 12px;
  margin-top: 4px;
  display: block;
  font-weight: 700;
`;

export const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

export const RemoveIcon = styled(CloseGrayIcon)`
  width: 16px;
  height: 16px;
`;

export const DropdownArrowIcon = styled(PaginationSelectIcon)`
  width: 16px;
  height: 16px;

  svg {
    fill: ${({ theme }) => theme.palette.text.primary};
  }
`;

export const StyledChip = styled(Chip)(
  ({ theme: { palette } }) => css`
    background-color: ${palette.grey[300]};
    color: ${palette.text.primary};
    font-weight: 700;
    font-size: 14px;
    height: 32px;
    border-radius: 16px;
    margin: 2px;
    padding: 0;

    & .MuiChip-label {
      padding: 6px 8px 6px 12px;
      font-weight: 700;
      color: ${palette.text.primary};
    }

    svg {
      margin-right: 6px;
    }
  `,
);

export const OptionNumber = styled(Typography)(
  ({ theme: { palette } }) => css`
    color: ${palette.grey[900]};
    font-size: 12px;
    font-weight: 700;
  `,
);

export const OptionLabel = styled(Typography)`
  font-size: 14px;
`;

export const ErrorWrapper = styled.div`
  margin-top: 8px;
`;
